<template>
  <b-modal
    ref="modalWarehouseMappingV2Modal"
    size="lg"
    body-class="!p-[16px]"
    no-close-on-backdrop
    no-close-on-esc
    centered
    hide-footer
    hide-header
  >
    <slot name="header">
      <h4 class="text-center text-[20px] font-semibold mt-[16px] mb-[24px] text-black">
        Mapping Gudang
      </h4>
    </slot>

    <div
      v-if="isLoading"
    >
      <div class="flex items-center justify-center gap-[16px] min-h-[200px]">
        <b-spinner
          variant="primary"
          label="memuat data gudang"
        />
      </div>
      <div class="flex items-center justify-end gap-[16px] mt-[16px]">
        <b-button
          variant="outline-primary"
          class="w-full"
          @click="handleCancel"
        >
          {{ cancelLabel }}
        </b-button>
      </div>
    </div>
    <div
      v-else-if="marketplaceWarehouseList.length === 0 || error"
      class="min-h-[200px] flex flex-col items-center justify-center"
    >
      <div class="flex-1 flex items-center justify-center">
        <div class="flex items-center flex-wrap max-w-[600px] flex-1 mx-auto justify-center gap-[8px] text-[#828282] text-[14px] mb-0 text-center">
          <span>{{ error ? error.message : 'Tidak ada data gudang.' }}</span>
          <b-button
            v-if="error && error.type === 'reauthentication_required'"
            variant="primary"
            size="sm"
            :disabled="isReauthenticating"
            @click="handleReauthenticate"
          >
            Autentikasi Ulang
          </b-button>
          <b-button
            v-else
            variant="primary"
            size="sm"
            @click="fetchMarketplaceWarehouseList"
          >
            Coba Lagi
          </b-button>
        </div>
      </div>
      <b-button
        variant="outline-primary"
        class="w-full"
        @click="handleCancel"
      >
        Tutup
      </b-button>
    </div>
    <div
      v-else
    >
      <div class="grid grid-cols-2 gap-[16px] text-[#333333] font-medium mb-[8px] bg-[#F4F4F4] p-[10px] rounded-[8px] -mx-[8px]">
        <span>Alamat Gudang di Marketplace</span>
        <span>Relasi Gudang</span>
      </div>
      <div>
        <div
          v-for="(warehouse, index) in marketplaceWarehouseList"
          :key="warehouse.marketplace_address_id"
          class="grid grid-cols-2 gap-[16px] border-b border-dashed border-[#E2E2E2] last:border-0 last:mb-0 last:pb-0 pb-[8px] mb-[8px]"
        >
          <div>
            <div class="flex items-center gap-[8px]">
              <h5 class="text-[#333333] text-[14px] mb-0">
                {{ warehouse.marketplace_warehouse_name }}
              </h5>
              <p class="text-[#333333] text-[14px] mb-[4px] rounded-full px-[8px] py-[2px] w-fit bg-[#F8F8F8] border border-[#E2E2E2] text-[11px] capitalize">
                {{ getWarehouseType(warehouse) }}
              </p>
            </div>
            <p class="text-[#828282] text-[14px] mb-0">
              {{ warehouse.marketplace_warehouse_address }}
            </p>
          </div>

          <div class="flex items-center justify-end">
            <infinite-select
              v-model="mappedWarehouses[index]"
              placeholder="Pilih Gudangmu"
              class="flex-1 bg-white"
              option-label="warehouse_name"
              :options="warehouseOptions"
              :has-next-page="warehouseFetcher.hasMore"
              :debounce-timeout="500"
              :loading="warehouseFetcher.isLoading"
              @on-search="handleSearchWarehouse"
              @on-intersect="warehouseFetcher.fetchData()"
            >
              <template #no-options="{ search, searching }">
                <template v-if="searching">
                  Gudang <em>'{{ search }}'</em> tidak ditemukan.
                </template>
                <em v-else>Masukkan nama gudang yang valid</em>
              </template>
            </infinite-select>
          </div>
        </div>
      </div>

      <div class="flex items-center justify-end gap-[16px] mt-[16px]">
        <b-button
          variant="outline-primary"
          class="w-full"
          @click="handleCancel"
        >
          {{ cancelLabel }}
        </b-button>
        <b-button
          :variant="noChanges ? 'secondary' : 'primary'"
          class="w-full !flex items-center justify-center gap-[8px]"
          :disabled="isSaving || noChanges"
          @click="handleSave"
        >
          <b-spinner
            v-if="isSaving"
            small
            variant="light"
          />
          Simpan
        </b-button>
      </div>

    </div>
  </b-modal>
</template>

<script>
/**
 * Modal mapping gudang marketplace ke gudang Komship (tanpa langkah pilih alamat toko dulu).
 *
 * @prop {Object} warehouseFetcher - Paginated fetcher gudang (mis. dari `createPaginatedFetcher`), wajib punya `data`, `hasMore`, `fetchData`, `isLoading`.
 * @emits success - Setelah batch simpan selesai; payload `Array<{ ok: boolean, error?: string }>` — periksa `ok` karena bisa sebagian gagal.
 *
 * @example
 * <ModalWarehouseMappingV2
 *   :warehouse-fetcher="subscribedWarehouseFetcher"
 *   @success="handleSuccessMappingWarehouse"
 * />
 */
import { asyncPool } from '@/libs/asyncPool'
import InfiniteSelect from '@/views/components/select/InfiniteSelect.vue'
import { isAxiosError } from 'axios'
import useMarketplaceIntegrationRequest from './composables/useMarketplaceIntegrationRequest'

export default {
  name: 'ModalWarehouseMappingV2',
  components: {
    InfiniteSelect,
  },
  props: {
    warehouseFetcher: {
      type: Object,
      required: true,
    },
    cancelLabel: {
      type: String,
      default: 'Batal',
    },
  },
  data() {
    return {
      isLoading: false,
      isSaving: false,
      shopDetails: null,
      marketplaceWarehouseList: [],
      mappedWarehouses: [],
      searchResults: [],
      error: null,
      isReauthenticating: false,
    }
  },
  computed: {
    warehouseOptions() {
      return this.searchResults.length > 0 ? this.searchResults : this.warehouseFetcher.data
    },
    /** ID gudang awal per baris (dari server) untuk diff terhadap pilihan user. */
    initialMappedWarehouses() {
      return this.marketplaceWarehouseList.map(row => row.warehouse_id ?? null)
    },
    /**
     * Hanya mapping yang benar-benar berubah, termasuk penghapusan gudang; bentuk body POST marketplace-shop-warehouses.
     */
    changedMappedWarehouses() {
      if (!this.shopDetails) {
        return {
          changed: [], deleted: [], mappedPickupCount: 0,
        }
      }
      return this.marketplaceWarehouseList.reduce((acc, warehouse, index) => {
        const initialId = this.initialMappedWarehouses[index]
        const current = this.mappedWarehouses[index]
        const currentId = current?.warehouse_id ?? null

        if (warehouse.type === 'pickup' && !!currentId) {
          acc.mappedPickupCount += 1
        }

        if (this.isSameWarehouseId(initialId, currentId)) return acc

        if (currentId == null) {
          acc.deleted.push(warehouse.id)
          return acc
        }

        const mpWhId = warehouse.marketplace_warehouse_id
        const payload = {
          marketplace_address_id: String(warehouse.marketplace_address_id),
          marketplace_warehouse_id: mpWhId != null && mpWhId !== '' ? String(mpWhId) : '',
          warehouse_id: currentId,
          shop_id: String(this.shopDetails.shop_id),
          marketplace: this.shopDetails.marketplace,
          type: warehouse.type || 'pickup',
        }

        acc.changed.push(payload)
        return acc
      }, {
        changed: [], deleted: [], mappedPickupCount: 0,
      })
    },
    noChanges() {
      return this.changedMappedWarehouses.changed.length === 0 && this.changedMappedWarehouses.deleted.length === 0
    },
  },
  methods: {
    /**
     * Membuka modal, memuat daftar gudang marketplace untuk `data` (shop), dan menampilkan `b-modal`.
     * Jika `warehouseFetcher` masih kosong, memanggil `fetchData()` sekali dulu.
     *
     * @param {{ shop_id: string|number, marketplace: string, [key: string]: unknown }} data - Konteks toko dari iframe / parent.
     */
    async show(data) {
      this.shopDetails = data
      this.error = null

      if (this.warehouseFetcher.data.length === 0) {
        await this.warehouseFetcher.fetchData()
      }

      this.fetchMarketplaceWarehouseList()
      await this.$nextTick()
      this.$refs.modalWarehouseMappingV2Modal.show()
    },
    /** Menutup modal dan mereset state lokal lewat {@link cleanup}. */
    hide() {
      this.$refs.modalWarehouseMappingV2Modal.hide()

      this.cleanup()
    },
    /** Mereset state form agar pembukaan berikutnya tidak membawa data toko / mapping lama. */
    cleanup() {
      this.shopDetails = null
      this.marketplaceWarehouseList = []
      this.mappedWarehouses = []
      this.searchResults = []
      this.error = null
      this.isSaving = false
    },
    /**
     * Mengisi `marketplaceWarehouseList` dan `mappedWarehouses` dari GET `/komship/api/v1/marketplace-shop-warehouses`.
     */
    async fetchMarketplaceWarehouseList() {
      try {
        this.isLoading = true
        this.error = null
        const { data: { data } } = await this.$http_new.get('/komship/api/v1/marketplace-shop-warehouses', {
          params: {
            shop_id: this.shopDetails.shop_id,
            marketplace: this.shopDetails.marketplace,
          },
        })

        this.marketplaceWarehouseList = data
        this.mappedWarehouses = data.map(({ warehouse_id, warehouse_name }) => ({
          warehouse_id,
          warehouse_name,
        }))
      } catch (error) {
        const errorMessage = isAxiosError(error) ? error?.response?.data?.meta?.message : null
        if (typeof errorMessage === 'string' && errorMessage.includes('reauthentication required')) {
          this.error = {
            message: `Diperlukan autentikasi ulang untuk toko ${this.shopDetails.shop_name} di marketplace ${this.shopDetails.marketplace}.`,
            type: 'reauthentication_required',
          }
          return
        }

        this.error = {
          message: 'Terjadi kesalahan saat mengambil data gudang.',
          type: 'general_error',
        }
      } finally {
        this.isLoading = false
      }
    },
    /**
     * Mengambil semua halaman `warehouseFetcher` secara berurutan sampai `hasMore` false.
     * Dipakai sebelum filter pencarian agar pencarian nama mencakup seluruh gudang yang sudah di-fetch.
     */
    async ensureWarehouseFetcherLoaded() {
      while (this.warehouseFetcher.hasMore) {
        // eslint-disable-next-line no-await-in-loop
        await this.warehouseFetcher.fetchData()
      }
    },
    /**
     * Filter client-side `warehouseFetcher.data` berdasarkan nama; memuat semua halaman fetcher jika ada query.
     *
     * @param {string} [search] - Teks dari `InfiniteSelect`; kosong mengembalikan daftar default (tanpa filter hasil).
     */
    async handleSearchWarehouse(search) {
      const q = (search ?? '').trim().toLowerCase()
      if (!q) {
        this.searchResults = []
        return
      }
      await this.ensureWarehouseFetcherLoaded()
      const searchResult = this.warehouseFetcher.data.filter(warehouse => (
        warehouse.warehouse_name.toLowerCase().includes(q)
      ))
      this.searchResults = searchResult.length > 0 ? searchResult : []
    },
    /**
     * Label UI untuk tipe baris gudang marketplace (`pickup` vs retur).
     * @param {{ type?: string }} warehouse
     * @returns {string}
     */
    getWarehouseType(warehouse) {
      if (warehouse.type === 'pickup') return 'Gudang Pengambilan'
      return 'Gudang Retur'
    },
    handleCancel() {
      this.$emit('cancel')
      this.hide()
    },
    /**
     * Perbandingan ID gudang aman untuk string/number/null (untuk deteksi perubahan mapping).
     * @param {string|number|null|undefined} a
     * @param {string|number|null|undefined} b
     */
    isSameWarehouseId(a, b) {
      if (a == null && b == null) return true
      if (a == null || b == null) return false
      return String(a) === String(b)
    },
    /**
     * Menyimpan hanya baris yang berubah (`changedMappedWarehouses`), max 10 request paralel per chunk (`asyncPool`).
     * Selalu mem-`$emit('success', results)`; parent boleh memeriksa `ok` per item. Toast sukses hanya jika semua `ok`.
     */
    async handleSave() {
      if (!this.shopDetails) return

      const changedPayloads = [
        ...this.changedMappedWarehouses.changed,
      ]

      if (this.noChanges) {
        this.hide()
        return
      }

      if (this.changedMappedWarehouses.mappedPickupCount <= 0) {
        this.$toast_error({ message: 'Gudang pengambilan tidak boleh kosong. Pengguna wajib melakukan mapping minimal satu gudang Kompack.' })
        return
      }

      this.isSaving = true
      try {
        const results = await asyncPool(10, changedPayloads, payload => this.updateMappedWarehouse(payload))

        const deletedResults = await asyncPool(10, this.changedMappedWarehouses.deleted, id => this.deleteMappedWarehouse(id))

        const failCount = results.filter(r => !r.ok).length + deletedResults.filter(r => !r.ok).length

        this.$emit('success', [...results, ...deletedResults])
        if (failCount === 0) {
          this.$toast_success({ message: 'Mapping gudang berhasil disimpan' })
          this.hide()
          return
        }
        const total = this.changedMappedWarehouses.changed.length + this.changedMappedWarehouses.deleted.length
        const message = failCount === total
          ? 'Gagal menyimpan mapping gudang. Silakan coba lagi.'
          : `${failCount} dari ${total} mapping gagal disimpan. Silakan coba lagi.`
        this.$toast_error({ message })
      } finally {
        this.isSaving = false
      }
    },
    /**
     * Satu request POST mapping; tidak melempar — mengembalikan `{ ok: true }` atau `{ ok: false, error }` untuk agregasi batch.
     * @param {Record<string, unknown>} data - Body sesuai kontrak `/komship/api/v1/marketplace-shop-warehouses` POST.
     * @returns {Promise<{ ok: true } | { ok: false, error?: string }>}
     */
    async updateMappedWarehouse(data) {
      try {
        await this.$http_new.post('/komship/api/v1/marketplace-shop-warehouses', data)
        return { ok: true }
      } catch (error) {
        return { ok: false, error: error?.message }
      }
    },
    /**
     * Satu request DELETE mapping; tidak melempar — mengembalikan `{ ok: true }` atau `{ ok: false, error }`.
     * @param {number} id - ID mapping yang akan dihapus.
     */
    async deleteMappedWarehouse(id) {
      try {
        await this.$http_new.delete(`/komship/api/v1/marketplace-shop-warehouses/${id}`)
        return { ok: true }
      } catch (error) {
        return { ok: false, error: error?.message }
      }
    },
    async handleReauthenticate() {
      if (!this.shopDetails?.marketplace) return
      this.isReauthenticating = true

      try {
        const { requestMarketplaceAuthUrl } = useMarketplaceIntegrationRequest(this)
        await requestMarketplaceAuthUrl(this.shopDetails.marketplace)
      } catch (error) {
        this.$toast_error({
          message: 'Gagal melakukan autentikasi ulang, silakan coba lagi.',
        })
      } finally {
        this.isReauthenticating = false
      }
    },
  },
}
</script>

<style lang="scss">
  @import '@core/scss/vue/libs/vue-select.scss';
</style>
